import React, { useState, useEffect } from 'react';
import StationEditDialog from './StationEditDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Divider,
  Alert
} from '@mui/material';
import {
  Edit,
  Delete,
  Close,
  Download,
  Warning
} from '@mui/icons-material';

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  is_owner?: boolean;
  tags?: string[];
}

interface StationConfigDialogProps {
  open: boolean;
  onClose: () => void;
  station?: Station;
  onEdit: (stationUuid: string, stationData: Partial<Station>) => Promise<void>;
  onDelete: (stationUuid: string, withExport: boolean) => void;
  onExport: (stationUuid: string) => void;
}

const StationConfigDialog: React.FC<StationConfigDialogProps> = ({
  open,
  onClose,
  station,
  onEdit,
  onDelete,
  onExport
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Reset state when station changes or dialog opens
  useEffect(() => {
    if (open) {
      setShowDeleteConfirm(false);
      setShowEditDialog(false);
    }
  }, [open, station?.uuid]);

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (stationData: Partial<Station>) => {
    if (station) {
      await onEdit(station.uuid, stationData);
    }
    setShowEditDialog(false);
  };

  const handleDelete = (withExport: boolean = false) => {
    if (station) {
      onDelete(station.uuid, withExport);
    }
    onClose();
  };

  const handleExport = () => {
    if (station) {
      onExport(station.uuid);
    }
    onClose();
  };

  if (!station) return null;

  // Check if current user is the owner of this station (from backend)
  const isOwner = station.is_owner === true;

  if (showDeleteConfirm) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h6">
              Delete Station
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete station "{station.name}"?
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action will permanently delete the station and all associated measurements.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Would you like to export the measurements to CSV first?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={() => handleDelete(true)}
              fullWidth
            >
              Export CSV & Delete
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
            Export includes up to 500 most recent records. For larger datasets, please contact our support team.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => handleDelete(false)}
              sx={{ flex: 1 }}
            >
              Delete Without Export
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  }

  if (showEditDialog) {
    return (
      <StationEditDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        station={station}
        onSave={handleSaveEdit}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Station Settings
          </Typography>
          <Button onClick={onClose} size="small" sx={{ minWidth: 'auto', p: 1 }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {station.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {station.description || 'No description'}
          </Typography>
        </Box>
        
        <Divider />

        <List sx={{ py: 1 }}>
          {isOwner ? (
            <>
              <ListItemButton onClick={handleEdit}>
                <ListItemIcon>
                  <Edit color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Edit Station"
                  secondary="Modify station details"
                />
              </ListItemButton>

              <ListItemButton onClick={handleExport}>
                <ListItemIcon>
                  <Download color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Export Measurements"
                  secondary="Download data in CSV format (up to 500 records)"
                />
              </ListItemButton>

              <Divider sx={{ my: 1 }} />

              <ListItemButton
                onClick={() => setShowDeleteConfirm(true)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                  }
                }}
              >
                <ListItemIcon>
                  <Delete color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography color="error">
                      Delete Station
                    </Typography>
                  }
                  secondary="Permanently delete station and measurements"
                />
              </ListItemButton>
            </>
          ) : (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">
                You can only view this station. Edit, export, and delete options are available only for stations you own.
              </Alert>
            </Box>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default StationConfigDialog;